"use client";

import { Copyright, Mail, MapPin, Phone } from "lucide-react";
import useOpenMaps from "@/components/hooks/use-open-maps";
import Container from "@/components/layout/Container";
import Flex from "@/components/layout/Flex";
import Section from "@/components/layout/Section";
import Logo from "@/components/media/Logo";
import Divider from "@/components/shared/Divider";
import Typography from "@/components/shared/Typography";
import Button from "@/components/ui/Button";
import {
  ADDRESS_CITY_STATE_ZIP,
  ADDRESS_STREET,
  HREF_EMAIL,
  HREF_PHONE_NUMBER,
  URLS,
} from "@/lib/consts";
import { breakpointClasses, cn, splitBreakpointProps } from "@/lib/utils";
import type { FooterProps } from "./types";

function Footer({ className, ...props }: FooterProps) {
  const openMaps = useOpenMaps();
  const { bp, native } = splitBreakpointProps(props);
  const classes = breakpointClasses(bp);

  return (
    <Section
      id="footer"
      className={cn("bg-card w-screen", className, classes)}
      {...native}
    >
      <Flex.Column asChild>
        <Container maxWidth="max-w-md" md="md:max-w-3xl" lg="lg:max-w-6xl">
          <Flex.Column md="md:flex-row" gap="~gap-4/8">
            <Flex.Column className="flex-1">
              <Logo />
              <Typography.Small>
                <span className="font-bold text-primary">
                  Orsak Langner & Barthel, PLLC.
                </span>{" "}
                is a full-service accounting firm in Houston, TX. Your business
                is unique and important. You need someone who is experienced,
                dynamic and who you can count on. We pride ourselves on
                providing a personalized accounting service that will adapt to
                meet your needs as you change and grow.
              </Typography.Small>
            </Flex.Column>

            <Flex.Column className="flex-1" gap="gap-0">
              <Typography.H4>Resources</Typography.H4>

              <Button.Link
                className="pl-0"
                variant="link"
                href={URLS.ABOUT}
                aria-label="Goto about page"
              >
                About
              </Button.Link>

              <Button.Link
                className="pl-0"
                variant="link"
                href={URLS.SERVICES.ROOT}
                aria-label="Goto services page"
              >
                Services
              </Button.Link>

              <Button.Link
                className="pl-0"
                variant="link"
                href={URLS.INDUSTRIES.ROOT}
                aria-label="Goto industries page"
              >
                Industries
              </Button.Link>

              <Button.Link
                className="pl-0"
                variant="link"
                href={URLS.SERVICES.TAX.IRS_RESOLUTION}
                aria-label="Goto IRS resolution page"
              >
                IRS Resolution
              </Button.Link>

              <Button.Link
                className="pl-0"
                variant="link"
                href={URLS.CONTACT_US}
                aria-label="Goto contact us page"
              >
                Contact Us
              </Button.Link>

              <Button.Link
                className="pl-0"
                variant="link"
                href={URLS.PRIVACY}
                aria-label="Goto privacy policy page"
              >
                Privacy Policy
              </Button.Link>
            </Flex.Column>

            <Flex.Column className="flex-1">
              <Typography.H4>Contact Details</Typography.H4>
              <Button.Link
                className="pl-0"
                variant="link"
                href={HREF_PHONE_NUMBER}
                aria-label="Click to call us"
              >
                <Phone />
                (713) 621-5000
              </Button.Link>
              <Button.Link
                className="pl-0"
                variant="link"
                href={HREF_EMAIL}
                aria-label="Click to email us"
              >
                <Mail />
                info@orsakcpas.com
              </Button.Link>

              <Flex.Row>
                <Button
                  variant="link"
                  onClick={openMaps}
                  className="pl-0 gap-2 w-full h-full"
                  aria-label="Click to open maps"
                >
                  <MapPin />

                  <Flex.Column fill gap="gap-" justify="center" items="start">
                    <Typography.Small>
                      Orsak Langner & Barthel PLLC
                    </Typography.Small>
                    <Typography.Small>{ADDRESS_STREET}</Typography.Small>
                    <Typography.Small>
                      {ADDRESS_CITY_STATE_ZIP}
                    </Typography.Small>
                  </Flex.Column>
                </Button>
              </Flex.Row>
            </Flex.Column>
          </Flex.Column>

          <Divider />

          <Flex.Row fill justify="end" items="stretch">
            <Copyright />
            <Typography.Small>
              <Typography.Small>
                2025 Orsak Langner & Barthel PLLC
              </Typography.Small>
            </Typography.Small>

            <Divider
              className="border-foreground"
              orientation="vertical"
              margin="m-0"
              thickness="border-l-2"
            />

            <Typography.Small>
              Designed & Crafted By KJ Web.Dev
            </Typography.Small>
          </Flex.Row>
        </Container>
      </Flex.Column>
    </Section>
  );
}

export default Footer;
